import React, { useEffect, useRef, useMemo } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";
import { BsArrowRight } from "react-icons/bs";
import { motion, useInView, useAnimation } from "framer-motion";
import "./HomeSolutions.css";

// External image URLs
const financeImg = "https://dazbog.ascentbw.com/Images/Home/comp1.png";
const projectImg = "https://dazbog.ascentbw.com/Images/Home/comp2.png";
const epcImg = "https://dazbog.ascentbw.com/Images/Home/comp3.png";
const emergingImg = "https://dazbog.ascentbw.com/Images/Home/comp4.png";

const DigitRoll = ({ digit, hideFirst, index }) => {
  const innerControls = useAnimation();
  const outerControls = useAnimation();
  const ref = useRef(null);

  // Trigger when it comes into view
  const inView = useInView(ref, { once: true, margin: "-10px" });

  // Generate a long sequence to create the "spinning" slot-machine effect
  const sequence = useMemo(() => {
    const arr = [];
    // Spin through 0-9 twice for the blur effect
    for (let i = 0; i < 2; i++) {
      for (let j = 0; j < 10; j++) {
        arr.push(j);
      }
    }
    // Stop exactly at the target digit
    for (let j = 0; j <= digit; j++) {
      arr.push(j);
    }
    return arr;
  }, [digit]);

  // Translate up by 1em per digit in the sequence
  const targetY = `-${sequence.length - 1}em`;

  // Stagger the animation so digits stop left-to-right
  const rollDuration = 2.0 + (index * 0.4);

  useEffect(() => {
    if (inView) {
      // Start rolling animation
      innerControls.start({
        y: targetY,
        transition: { duration: rollDuration, ease: [0.22, 1, 0.36, 1] }
      });

      // Hide the leading zero during the rolling animation
      if (hideFirst) {
        setTimeout(() => {
          outerControls.start({
            width: 0,
            opacity: 0,
            marginRight: 0,
            transition: { duration: 1.2, ease: [0.22, 1, 0.36, 1] }
          });
        }, 1200); // Start hiding while it's still spinning
      }
    }
  }, [inView, innerControls, outerControls, targetY, hideFirst, rollDuration]);

  return (
    <motion.div
      className="digit-roll-container"
      animate={outerControls}
    >
      <motion.div
        ref={ref}
        className="digit-roll-strip"
        initial={{ y: "0em" }}
        animate={innerControls}
      >
        {sequence.map((n, i) => (
          <div key={i} className="digit-unit">
            {n}
          </div>
        ))}
      </motion.div>
    </motion.div>
  );
};

const RollingNumber = ({ value }) => {
  // Find the numeric part to animate
  const match = value.match(/(\d+)/);
  if (!match) return <div className="rolling-number">{value}</div>;

  const numberPart = match[1];
  const prefix = value.substring(0, match.index);
  const suffix = value.substring(match.index + numberPart.length);

  // Add one leading zero to collapse dynamically
  const displayDigits = ("0" + numberPart).split("");

  return (
    <div className="rolling-number">
      {prefix && <span className="static-char">{prefix}</span>}
      {displayDigits.map((char, index) => (
        <DigitRoll
          key={index}
          index={index}
          digit={parseInt(char, 10)}
          hideFirst={index === 0}
        />
      ))}
      {suffix && <span className="static-char">{suffix}</span>}
    </div>
  );
};

const HomeSolutions = () => {
  const solutions = [
    {
      title: "Financial & Transaction Advisory",
      description: "Support for project finance, investment planning, refinancing, and renewable energy transactions.",
      image: financeImg,
      link: "/services/finance",
    },
    {
      title: "Project Development & Technical Services",
      description: "Technical reviews, project studies, engineering support, and planning for renewable projects.",
      image: projectImg,
      link: "/services/project-development",
    },
    {
      title: "EPC Coordination & Execution",
      description: "Project coordination and execution support from planning to commissioning.",
      image: epcImg,
      link: "/services/epc-coordination",
    },
    {
      title: "Emerging Energy Solutions",
      description: "Support for BESS, hybrid renewable projects, and green hydrogen initiatives.",
      image: emergingImg,
      link: "/services/emerging",
    },
  ];

  return (
    <section className="home-solutions-section">
      {/* About Us Summary Section */}
      <div className="about-summary-wrapper">
        <Container>
          <Row className="align-items-start">
            <Col lg={7} md={12} className="about-text-col">
              <span className="section-tag" style={{color:"white"}}>About Us</span>
              <h2 className="about-main-heading">
                supporting renewable energy projects through finance, engineering, and execution expertise.
              </h2>
            </Col>
            <Col lg={5} md={12} className="stats-col">
              <div className="stats-container">
                <div className="stat-item">
                  <span className="stat-label1">Renewable transactions supported</span>
                  <div className="stat-value">
                    <RollingNumber value="$1B+" />
                  </div>
                </div>

                <div className="stat-divider"></div>
                <div className="stat-item">
                  <span className="stat-label1">Engineering mandates delivered</span>
                  <div className="stat-value">
                    <RollingNumber value="40+" />
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <Container>
        {/* Our Solutions Section */}
        <div className="solutions-header text-center">
          <div className="solutions-tag">
            <span>Our Solution</span>
          </div>
          <h2 className="solutions-main-title">
            Supporting Renewable Energy Projects <br className="d-none d-md-block" />
            from Planning to Execution
          </h2>
          <p className="solutions-subtitle">
            We help developers, investors, and businesses with technical, financial, <br className="d-none d-md-block" />
            and project support across renewable energy projects.
          </p>
          <div className="explore-btn-wrapper">
            <Link to="#" onClick={(e) => e.preventDefault()} className="explore-solutions-btn">
              Explore All Solutions
            </Link>
          </div>
        </div>

        {/* Solutions Grid */}
        <Row className="solutions-grid">
          {solutions.map((item, index) => (
            <Col key={index} lg={6} md={6} sm={12} className="mb-4">
              <Link to="#" onClick={(e) => e.preventDefault()} className="solution-card-link">
                <div className="solution-card">
                  <div className="card-image-wrapper">
                    <img src={item.image} alt={item.title} className="card-img" />
                    <div className="card-arrow-btn">
                      <BsArrowRight />
                    </div>
                  </div>
                  <div className="card-content">
                    <h4 className="card-title1">{item.title}</h4>
                    <p className="card-desc1">{item.description}</p>
                  </div>
                </div>
              </Link>
            </Col>
          ))}
        </Row>
      </Container>
    </section>
  );
};

export default HomeSolutions;